/**
 * Utilitário centralizado para tratamento de erros
 * Padroniza mensagens de erro e comportamento em toda a aplicação
 */
import { safeStringify } from "./safeStringify";

export enum ErrorType {
  NETWORK = "network",
  AUTHENTICATION = "authentication",
  AUTHORIZATION = "authorization",
  VALIDATION = "validation",
  SERVER = "server",
  CLIENT = "client",
  TIMEOUT = "timeout",
  UNKNOWN = "unknown",
}

export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: any;
  statusCode?: number;
  retryable?: boolean;
  userFriendlyMessage: string;
}

export class ErrorHandler {
  /**
   * Converte erro HTTP em AppError padronizado
   */
  static fromHttpResponse(response: Response, responseData?: any): AppError {
    const statusCode = response.status;

    // Se a resposta for HTML em vez de JSON, provavelmente é um erro de roteamento/proxy (como 404 do Nginx ou Next.js)
    const isHtml =
      typeof responseData === "string" &&
      (responseData.trim().startsWith("<!DOCTYPE") ||
        responseData.trim().startsWith("<html"));
    const sanitizedData = isHtml
      ? { message: "Falha na conexão com o servidor (Resposta Inválida)." }
      : responseData;

    // Erros de autenticação/autorização
    if (statusCode === 401) {
      return {
        type: ErrorType.AUTHENTICATION,
        message: "Token de autenticação expirado ou inválido",
        statusCode,
        retryable: false,
        userFriendlyMessage: "Sua sessão expirou. Faça login novamente.",
      };
    }

    if (statusCode === 403) {
      return {
        type: ErrorType.AUTHORIZATION,
        message: "Acesso negado",
        statusCode,
        retryable: false,
        userFriendlyMessage: "Você não tem permissão para realizar esta ação.",
      };
    }

    // Erros de validação
    if (statusCode === 400) {
      const message =
        sanitizedData?.error || sanitizedData?.message || "Dados inválidos";
      return {
        type: ErrorType.VALIDATION,
        message,
        statusCode,
        retryable: false,
        userFriendlyMessage: message,
      };
    }

    // Erros do servidor (5xx) - geralmente retryable
    if (statusCode >= 500) {
      // Preservar mensagem específica do backend quando disponível
      const backendMessage = sanitizedData?.error || sanitizedData?.message;
      // Incluir detalhes se disponíveis
      const details = sanitizedData?.details;
      let fullMessage = backendMessage || "Erro interno do servidor";
      if (details) {
        if (typeof details === "string") {
          fullMessage = `${fullMessage}: ${details}`;
        } else if (typeof details === "object") {
          fullMessage = `${fullMessage}: ${safeStringify(details)}`;
        }
      }
      return {
        type: ErrorType.SERVER,
        message: fullMessage,
        statusCode,
        retryable: true,
        userFriendlyMessage:
          "O servidor encontrou um erro inesperado. Tente novamente mais tarde!",
      };
    }

    // Erros 404 - recurso não encontrado
    if (statusCode === 404) {
      const backendMessage = sanitizedData?.error || sanitizedData?.message;
      return {
        type: ErrorType.NETWORK,
        message: backendMessage || "Recurso não encontrado",
        statusCode,
        retryable: false,
        userFriendlyMessage:
          backendMessage ||
          "Falha na conexão com o servidor (404). Verifique se o backend está rodando corretamente ou se há conflito de portas.",
      };
    }

    // Erro 429 - Quota/Rate Limit excedida
    if (statusCode === 429) {
      const backendMessage = responseData?.error || responseData?.message;

      // Preservar detalhes completos do erro de quota para uso no componente
      const error: AppError = {
        type: ErrorType.NETWORK,
        message: backendMessage || "Quota da API excedida",
        statusCode,
        retryable: false, // Quota não deve ser retryable imediatamente
        userFriendlyMessage:
          backendMessage ||
          "Quota da API excedida. Tente novamente mais tarde.",
        originalError: responseData, // Preservar dados completos para tratamento específico
      };

      return error;
    }

    // Erros de rede (4xx restantes)
    if (statusCode >= 400) {
      const backendMessage = responseData?.error || responseData?.message;
      return {
        type: ErrorType.NETWORK,
        message: backendMessage || `Erro ${statusCode}`,
        statusCode,
        retryable: false,
        userFriendlyMessage:
          backendMessage || "Ocorreu um erro na solicitação.",
      };
    }

    // Fallback
    return {
      type: ErrorType.UNKNOWN,
      message: "Erro desconhecido",
      statusCode,
      retryable: false,
      userFriendlyMessage: "Ocorreu um erro inesperado.",
    };
  }

  /**
   * Converte erro genérico em AppError padronizado
   */
  static fromError(error: any): AppError {
    // Network errors
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      return {
        type: ErrorType.NETWORK,
        message: error.message || "Erro de conexão",
        originalError: error,
        retryable: true,
        userFriendlyMessage:
          "Não foi possível conectar ao servidor. Ele pode estar temporariamente offline ou em manutenção — tente novamente em alguns instantes.",
      };
    }

    // Timeout errors
    const isTimeout =
      error.name === "AbortError" ||
      (error.message && error.message.toLowerCase().includes("timeout"));

    if (isTimeout) {
      return {
        type: ErrorType.TIMEOUT,
        message: "Tempo limite excedido",
        originalError: error,
        retryable: true,
        userFriendlyMessage: "A solicitação demorou muito. Tente novamente.",
      };
    }

    // JSON parsing errors
    if (error instanceof SyntaxError && error.message.includes("JSON")) {
      return {
        type: ErrorType.SERVER,
        message: "Resposta inválida do servidor",
        originalError: error,
        retryable: true,
        userFriendlyMessage: "Resposta inválida do servidor. Tente novamente.",
      };
    }

    // Se o erro já tem statusCode e mensagem, preservar (erro já tratado em fromHttpResponse)
    if ((error as any)?.statusCode && error.message) {
      return {
        type: ErrorType.NETWORK,
        message: error.message,
        originalError: error,
        statusCode: (error as any).statusCode,
        retryable: (error as any).statusCode === 429 ? false : false,
        userFriendlyMessage: error.message,
      };
    }

    // Fallback para erros genéricos
    return {
      type: ErrorType.UNKNOWN,
      message: error.message || "Erro desconhecido",
      originalError: error,
      retryable: false,
      userFriendlyMessage: "Ocorreu um erro inesperado. Tente novamente.",
    };
  }

  /**
   * Determina se um erro deve ser tentado novamente
   */
  static isRetryable(error: AppError): boolean {
    return error.retryable || false;
  }

  /**
   * Log detalhado do erro para desenvolvimento
   */
  static logError(error: AppError, context?: string): void {
    const logData = {
      type: error.type,
      message: error.message,
      statusCode: error.statusCode,
      retryable: error.retryable,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    // Erros de validação (400) e quota (429) são esperados e tratados - usar warn em vez de error
    // para reduzir ruído no console
    if (
      (error.type === ErrorType.VALIDATION && error.statusCode === 400) ||
      error.statusCode === 429
    ) {
      console.warn(
        `⚠️ [${error.type.toUpperCase()}] ${error.message}`,
        logData,
      );
    } else {
      console.error(
        `❌ [${error.type.toUpperCase()}] ${error.message}`,
        logData,
      );
    }

    // Em produção, isso poderia ser enviado para um serviço de logging
    // if (process.env.NODE_ENV === 'production') {
    //   logToService(logData);
    // }
  }

  /**
   * Cria mensagem de erro para toast/snackbar
   */
  static getToastMessage(error: AppError): string {
    return error.userFriendlyMessage;
  }

  /**
   * Determina o tipo de toast baseado no erro
   */
  static getToastType(error: AppError): "error" | "warning" | "info" {
    switch (error.type) {
      case ErrorType.AUTHENTICATION:
      case ErrorType.AUTHORIZATION:
        return "warning";
      case ErrorType.NETWORK:
      case ErrorType.TIMEOUT:
        return "info";
      default:
        return "error";
    }
  }
}

/**
 * Hook personalizado para tratamento de erros em componentes
 */
export function useErrorHandler() {
  const handleError = (error: any, context?: string) => {
    const appError = error.status
      ? ErrorHandler.fromHttpResponse(error, error.data)
      : ErrorHandler.fromError(error);
    ErrorHandler.logError(appError, context);
    return appError;
  };

  return { handleError };
}
